/* ────────────────────────────────────────────────────────
   The category archives.

   Each list is the real one from that category's page on ejinsight.com — the
   six QA gave as the reference:

     Hong Kong  /eji/category/hongkong
     Business   /eji/category/business
     World      /eji/category/world
     Lifestyle  /eji/category/living
     Features   /eji/category/features
     Startups   /eji/category/startups

   The listings used to render whatever the HOME page's section pool held —
   four rows each, because that is how many the home block shows. QA: "for the
   Hong Kong, Business, World, Lifestyle, Features and startups categories, add
   more articles". The home blocks still take their four from the top of these
   lists, so the two cannot disagree.

   As with the columnist roster: the years are shifted onto this demo's
   2025–2026 timeline, the thumbnails cycle the section imagery, and the
   Features list is the English rendering of headlines the source publishes in
   Chinese — this vertical is EJ Insight's English edition.
   ──────────────────────────────────────────────────────── */
export type CategoryArchiveItem = {
  title: string;
  author?: string;
  date: string;
  img: string;
};

export const CATEGORY_ARCHIVES: Record<string, CategoryArchiveItem[]> = {
  /* HONG KONG carries twenty, sixteen of them inside the last thirty days.
     Two QA notes meet here: the listings default to "Last 30 days", and this
     category has to show "the new sidebar and pagination effect" — which needs
     more than twelve rows to survive that default. Sixteen gives a full first
     page of twelve with its editorial rail and a second page of four with an ad
     rail; the four older ones are left outside the window so the filter visibly
     does something. The dates are the demo timeline's, as everywhere else in
     this file. */
  "hong-kong": [
    {
      title: "How should leaders govern the people?",
      author: "Michael Chugani",
      date: "August 31, 2026",
      img: "/images/ejinsight/hk-1.png",
    },
    {
      title: "Hong Kong Tourism Board Unveils 'Only in Hong Kong'",
      date: "August 29, 2026",
      img: "/images/ejinsight/hk-2.png",
    },
    {
      title: "Zhang Daqian, China's Best Painter for 500 Years",
      author: "Mark O'Neill",
      date: "August 27, 2026",
      img: "/images/ejinsight/hk-3.png",
    },
    {
      title: "Government celebrates success of Kai Tak Sports Park",
      author: "Mark O'Neill",
      date: "August 25, 2026",
      img: "/images/ejinsight/hk-4.png",
    },
    {
      title: "HK people in UK fear new immigration rules",
      author: "Mark O'Neill",
      date: "August 23, 2026",
      img: "/images/ejinsight/hk-5.png",
    },
    {
      title: "Does Age Matter in Politics?",
      author: "Michael Chugani",
      date: "August 21, 2026",
      img: "/images/ejinsight/hk-1.png",
    },
    {
      title: "Should HK limit overtourism?",
      author: "Michael Chugani",
      date: "August 19, 2026",
      img: "/images/ejinsight/hk-2.png",
    },
    {
      title: "Hongkongers face uncertain future after Farage promise",
      author: "Mark O'Neill",
      date: "August 17, 2026",
      img: "/images/ejinsight/hk-3.png",
    },
    {
      title: "Number of HK migrants to Britain falls",
      author: "Mark O'Neill",
      date: "August 15, 2026",
      img: "/images/ejinsight/hk-4.png",
    },
    {
      title: "A Macanese family – from Portugal to the world",
      author: "Mark O'Neill",
      date: "August 13, 2026",
      img: "/images/ejinsight/hk-5.png",
    },
    {
      title: "Hong Kong Turns Green for a Day",
      author: "Mark O'Neill",
      date: "August 11, 2026",
      img: "/images/ejinsight/hk-2.png",
    },
    {
      title: "First two HK martyrs in Ukraine war were “so brave, so selfless”",
      author: "Mark O'Neill",
      date: "August 9, 2026",
      img: "/images/ejinsight/hk-3.png",
    },
    {
      title: "How the Irish Made Hong Kong",
      author: "Mark O'Neill",
      date: "August 7, 2026",
      img: "/images/ejinsight/hk-4.png",
    },
    {
      title: "Government rewrites history of Hong Kong",
      author: "Mark O'Neill",
      date: "August 5, 2026",
      img: "/images/ejinsight/hk-5.png",
    },
    {
      title: "Hong Kong: A Tale of Three Cities?",
      author: "Michael Chugani",
      date: "August 3, 2026",
      img: "/images/ejinsight/hk-1.png",
    },
    {
      title: "The Trump-Mamdani bromance",
      author: "Michael Chugani",
      date: "August 2, 2026",
      img: "/images/ejinsight/hk-2.png",
    },
    {
      title: "Is America a broken nation?",
      author: "Michael Chugani",
      date: "June 12, 2026",
      img: "/images/ejinsight/hk-3.png",
    },
    {
      title: "Rank dishonour",
      author: "Neville Sarony",
      date: "April 3, 2026",
      img: "/images/ejinsight/hk-4.png",
    },
    {
      title: "Contempt for justice",
      author: "Neville Sarony",
      date: "January 22, 2026",
      img: "/images/ejinsight/hk-5.png",
    },
    {
      title: "Re-imagining Hong Kong's role in the Greater Bay Area",
      author: "Brian Y. S. Wong",
      date: "November 5, 2025",
      img: "/images/ejinsight/hk-1.png",
    },
  ],
  "business": [
    {
      title: "Taiwan to post highest GDP growth in 39 years",
      author: "Mark O'Neill",
      date: "August 31, 2026",
      img: "/images/ejinsight/business-1.png",
    },
    {
      title: "EU becomes largest foreign investor in Taiwan",
      author: "Mark O'Neill",
      date: "August 29, 2026",
      img: "/images/ejinsight/business-2.png",
    },
    {
      title: "Ireland becomes semiconductor hub in Europe",
      author: "Mark O'Neill",
      date: "August 27, 2026",
      img: "/images/ejinsight/business-3.png",
    },
    {
      title: "Will BYD replace Toyota as top global automaker?",
      author: "Mark O'Neill",
      date: "August 25, 2026",
      img: "/images/ejinsight/business-4.png",
    },
    {
      title: "Poland economy to surpass U.K., France",
      date: "August 23, 2026",
      img: "/images/ejinsight/business-1.png",
    },
    {
      title: "Tong Ren Tang Healthcare targets Hong Kong IPO",
      date: "August 21, 2026",
      img: "/images/ejinsight/business-2.png",
    },
    {
      title: "China and EU hurtle toward a trade war",
      author: "Mark O'Neill",
      date: "August 19, 2026",
      img: "/images/ejinsight/business-3.png",
    },
    {
      title: "Taiwan economy takes flight with AI boom",
      date: "August 17, 2026",
      img: "/images/ejinsight/business-4.png",
    },
    {
      title: "Germany fights to save auto industry from China",
      author: "Mark O'Neill",
      date: "June 12, 2026",
      img: "/images/ejinsight/business-1.png",
    },
    {
      title: "Fiscal dominance and negative rates risk",
      date: "April 3, 2026",
      img: "/images/ejinsight/business-2.png",
    },
    {
      title: "AI: it's time to execute, not experiment — but is your data ready?",
      date: "January 22, 2026",
      img: "/images/ejinsight/business-3.png",
    },
    {
      title: "TSMC invests billions in plant upgrade in Japan",
      author: "Mark O'Neill",
      date: "November 5, 2025",
      img: "/images/ejinsight/business-4.png",
    },
  ],
  "world": [
    {
      title: "China's rich tremble at taxes, exit bans",
      author: "Mark O'Neill",
      date: "August 31, 2026",
      img: "/images/ejinsight/world-1.png",
    },
    {
      title: "In Russia, Chinese workers have privilege to protest",
      author: "Mark O'Neill",
      date: "August 29, 2026",
      img: "/images/ejinsight/world-2.png",
    },
    {
      title: "Beijing may cancel September summit with Trump",
      author: "Mark O'Neill",
      date: "August 27, 2026",
      img: "/images/ejinsight/world-3.png",
    },
    {
      title: "Western liberalism: fuelling the rise of political strongmen",
      date: "August 25, 2026",
      img: "/images/ejinsight/world-4.png",
    },
    {
      title: "What do North Korean soldiers get from the war?",
      author: "Mark O'Neill",
      date: "August 23, 2026",
      img: "/images/ejinsight/world-1.png",
    },
    {
      title: "Putin prepares Forever War",
      author: "Mark O'Neill",
      date: "August 21, 2026",
      img: "/images/ejinsight/world-2.png",
    },
    {
      title: "China-Taiwan war would cost US$2 trillion",
      author: "Mark O'Neill",
      date: "August 19, 2026",
      img: "/images/ejinsight/world-3.png",
    },
    {
      title: "America at 250: A bitterly divided nation",
      author: "Michael Chugani",
      date: "August 17, 2026",
      img: "/images/ejinsight/world-4.png",
    },
    {
      title: "Will French vote for convict as President?",
      author: "Mark O'Neill",
      date: "June 12, 2026",
      img: "/images/ejinsight/world-1.png",
    },
    {
      title: "Britain's new premier brings qualities to the job",
      date: "April 3, 2026",
      img: "/images/ejinsight/world-2.png",
    },
    {
      title: "Is Britain becoming a Third World country?",
      author: "Mark O'Neill",
      date: "January 22, 2026",
      img: "/images/ejinsight/world-3.png",
    },
    {
      title: "Will Ukraine retake Crimea?",
      author: "Mark O'Neill",
      date: "November 5, 2025",
      img: "/images/ejinsight/world-4.png",
    },
  ],
  "lifestyle": [
    {
      title: "HK Ballet's excellent new Sleeping Beauty",
      author: "Kevin Ng",
      date: "August 31, 2026",
      img: "/images/ejinsight/lifestyle-1.png",
    },
    {
      title: "HK Ballet's Glam Rock",
      author: "Kevin Ng",
      date: "August 29, 2026",
      img: "/images/ejinsight/lifestyle-2.png",
    },
    {
      title: "Peony Pavilion from Suzhou",
      author: "Kevin Ng",
      date: "August 27, 2026",
      img: "/images/ejinsight/lifestyle-3.png",
    },
    {
      title: "Modern dance delights in HK Arts Festival",
      author: "Kevin Ng",
      date: "August 25, 2026",
      img: "/images/ejinsight/lifestyle-4.png",
    },
    {
      title: "Roberto Bolle starred in HK Arts Festival",
      author: "Kevin Ng",
      date: "August 23, 2026",
      img: "/images/ejinsight/lifestyle-1.png",
    },
    {
      title: "Camilo Pessanha's Macau stories: a poet and his city",
      date: "August 21, 2026",
      img: "/images/ejinsight/lifestyle-2.png",
    },
    {
      title: "National Ballet of China on top form in HK",
      author: "Kevin Ng",
      date: "August 19, 2026",
      img: "/images/ejinsight/lifestyle-3.png",
    },
    {
      title: "Hong Kong Ballet's year of highs and lows",
      author: "Kevin Ng",
      date: "August 17, 2026",
      img: "/images/ejinsight/lifestyle-4.png",
    },
    {
      title: "Hong Kong Dance Company's tribute to Bruce Lee",
      author: "Kevin Ng",
      date: "June 12, 2026",
      img: "/images/ejinsight/lifestyle-1.png",
    },
    {
      title: "Hong Kong Ballet's spectacular Gala & Romeo",
      author: "Kevin Ng",
      date: "April 3, 2026",
      img: "/images/ejinsight/lifestyle-2.png",
    },
    {
      title: "Shanghai Dance Company's new Song dynasty drama",
      author: "Kevin Ng",
      date: "January 22, 2026",
      img: "/images/ejinsight/lifestyle-3.png",
    },
    {
      title: "Star-studded premiere of Hong Kong Ballet's new Giselle",
      author: "Kevin Ng",
      date: "November 5, 2025",
      img: "/images/ejinsight/lifestyle-4.png",
    },
  ],
  "features": [
    {
      title: "Protect yourself from dual attacks of pneumococcal disease and flu",
      date: "August 31, 2026",
      img: "/images/ejinsight/features-1.png",
    },
    {
      title: "Cultural fusion across Guangdong, Hong Kong and Macau",
      date: "August 29, 2026",
      img: "/images/ejinsight/lifestyle-3.png",
    },
    {
      title: "Blending the Greater Bay Area's many cultures",
      date: "August 27, 2026",
      img: "/images/ejinsight/lifestyle-4.png",
    },
    {
      title: "Elīna Garanča makes her Hong Kong debut as Carmen",
      date: "August 25, 2026",
      img: "/images/ejinsight/hk-5.png",
    },
    {
      title: "Cello and piano at their most compelling",
      date: "August 23, 2026",
      img: "/images/ejinsight/features-1.png",
    },
    {
      title: "A French botanical hair-care specialist returns to Hong Kong",
      date: "August 21, 2026",
      img: "/images/ejinsight/lifestyle-3.png",
    },
    {
      title: "Chu Chi-chi's orchestra salutes two screen-music legends",
      date: "August 19, 2026",
      img: "/images/ejinsight/lifestyle-4.png",
    },
    {
      title: "Mulan retold on stage, and the nine sons of the dragon reimagined",
      date: "August 17, 2026",
      img: "/images/ejinsight/hk-5.png",
    },
    {
      title: "Leading musicians serve up a superb evening",
      date: "June 12, 2026",
      img: "/images/ejinsight/features-1.png",
    },
    {
      title: "Born and Bred x Fireside: Asia's 50 Best comes to Hong Kong",
      date: "April 3, 2026",
      img: "/images/ejinsight/lifestyle-3.png",
    },
    {
      title: "An August-only Yunnan pu'er tea banquet",
      date: "January 22, 2026",
      img: "/images/ejinsight/lifestyle-4.png",
    },
    {
      title: "Yakiniku Horumon Matchan opens its first branch outside Japan",
      date: "November 5, 2025",
      img: "/images/ejinsight/hk-5.png",
    },
  ],
  "startups": [
    {
      title: "Anthropic releases new AI model",
      date: "August 31, 2026",
      img: "/images/ejinsight/cat-anthropic.jpg",
    },
    {
      title: "Using a smart assistant to manage health",
      author: "Dr. Winnie Tang",
      date: "August 29, 2026",
      img: "/images/ejinsight/business-2.png",
    },
    {
      title: "The secret of staying ahead in the tech world for over 50 years",
      author: "Dr. Winnie Tang",
      date: "August 27, 2026",
      img: "/images/ejinsight/business-4.png",
    },
    {
      title: "Hong Kong start-up wins first Deignan Award",
      author: "Mark O'Neill",
      date: "August 25, 2026",
      img: "/images/ejinsight/world-2.png",
    },
    {
      title: "Food waste reduction alleviates landfill dilemma",
      author: "Dr. Winnie Tang",
      date: "August 23, 2026",
      img: "/images/ejinsight/cat-anthropic.jpg",
    },
    {
      title: "Cyber security startup SandboxAQ raises US$500 million",
      date: "August 21, 2026",
      img: "/images/ejinsight/business-2.png",
    },
    {
      title: "Environmental performance of old buildings needs to improve",
      author: "Dr. Winnie Tang",
      date: "June 12, 2026",
      img: "/images/ejinsight/business-4.png",
    },
    {
      title: "Emissions reduction a top priority for the construction industry",
      author: "Dr. Winnie Tang",
      date: "April 3, 2026",
      img: "/images/ejinsight/world-2.png",
    },
    {
      title: "Pros and cons of synthetic data",
      author: "Dr. Winnie Tang",
      date: "January 22, 2026",
      img: "/images/ejinsight/cat-anthropic.jpg",
    },
    {
      title: "Vista Equity to take insurtech Duck Creek private",
      date: "November 5, 2025",
      img: "/images/ejinsight/business-2.png",
    },
  ],
};
